import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ImagePlus, Trash2 } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { deleteGalleryAlbum, deleteGalleryPhoto } from '../../lib/admin-pin-client';
import { albumDeletedToast, photoDeletedToast, pushScoringToast } from '../common/admin-toasts';
import { usePinDelete } from '../common/PinDeleteDialog';
import { apiUrl } from '../../lib/api-base';
import { prepareContentPhotoFile } from '../../lib/event-flyer-compress';
import { useApiJson } from '../../hooks/useApi';
import { cn } from '../../lib/utils';

interface GalleryPhoto {
  id: string;
  url: string | null;
  width: number | null;
  height: number | null;
  alt: string;
  caption: string | null;
  position: number;
}

interface GalleryAlbum {
  id: string;
  slug: string;
  title: string;
  date: string;
  category: string;
  description: string;
  coverPhotoId: string | null;
  publishState: 'draft' | 'published';
  photos: GalleryPhoto[];
}

interface AlbumsDto {
  ok: boolean;
  albums: GalleryAlbum[];
}

const inputClass =
  'w-full min-h-11 bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3.5 text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] text-sm';

function photoCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'photo' : 'photos'}`;
}

function adminPhotoPath(url: string): string {
  return url.replace('/api/public/gallery/photos/', '/api/gallery/photos/');
}

function GalleryThumb({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      setSrc(url);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    fetch(apiUrl(adminPhotoPath(url)), { credentials: 'include' })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error('missing'))))
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) URL.revokeObjectURL(objectUrl);
        else setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (!src) return <div className="aspect-square rounded-[var(--radius-sm)] bg-[var(--color-surface-2)]" />;
  return <img src={src} alt="" className="aspect-square w-full rounded-[var(--radius-sm)] object-cover" />;
}

export function GalleryManagement() {
  const { data, mutate } = useApiJson<AlbumsDto>('/api/gallery/albums');
  const [filter, setFilter] = useState<'all' | 'draft' | 'published'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const pinDelete = usePinDelete();

  const albums = (data?.albums ?? []).filter((album) => !removedIds.has(album.id));
  const visible = albums.filter((album) => filter === 'all' || album.publishState === filter);
  const selected = albums.find((album) => album.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      {pinDelete.dialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Album status">
          {(['all', 'published', 'draft'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={cn(
                'min-h-11 rounded-[var(--radius-md)] px-3 text-sm font-medium capitalize',
                filter === value
                  ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]',
              )}
            >
              {value}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="min-h-11 rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm font-medium text-white"
          onClick={() => {
            setCreating(true);
            setSelectedId(null);
            setError(null);
          }}
        >
          New album
        </button>
      </div>
      {error && <p className="text-sm text-[var(--color-error-text)]">{error}</p>}
      {creating && (
        <AlbumDraft
          onCancel={() => setCreating(false)}
          onCreated={async (album) => {
            await mutate();
            setCreating(false);
            setSelectedId(album.id);
          }}
          onError={setError}
        />
      )}
      {!selected && !creating && (
        <ul className="divide-y divide-[var(--color-border)] rounded-[var(--radius-md)] border border-[var(--color-border)]">
          {visible.length === 0 && (
            <li className="px-4 py-8 text-sm text-[var(--color-text-secondary)]">No albums in this filter.</li>
          )}
          {visible.map((album) => (
            <li key={album.id}>
              <button
                type="button"
                className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--color-surface-2)]"
                onClick={() => setSelectedId(album.id)}
              >
                <span>
                  <span className="block text-sm font-medium text-[var(--color-text-primary)]">{album.title}</span>
                  <span className="text-xs text-[var(--color-text-tertiary)]">
                    {album.date} · {album.category || 'Uncategorised'} · {photoCountLabel(album.photos.filter((photo) => photo.position >= 0).length)}
                  </span>
                </span>
                <span className="text-xs capitalize text-[var(--color-text-secondary)]">{album.publishState}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {selected && (
        <AlbumEditor
          key={selected.id}
          album={selected}
          onBack={() => setSelectedId(null)}
          onChange={mutate}
          onError={setError}
          askDelete={pinDelete.ask}
          onDeleted={() => {
            setRemovedIds((prev) => new Set(prev).add(selected.id));
            setSelectedId(null);
          }}
        />
      )}
    </div>
  );
}

function AlbumDraft({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: (album: GalleryAlbum) => void;
  onError: (message: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  return (
    <form
      className="space-y-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-4"
      onSubmit={async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
          const res = await apiFetch<{ album: GalleryAlbum }>('/api/gallery/albums', {
            method: 'POST',
            body: JSON.stringify({ title, date, category, publishState: 'draft' }),
          });
          onCreated(res.album);
        } catch (err) {
          onError(err instanceof Error ? err.message : 'Could not create the album');
        } finally {
          setSaving(false);
        }
      }}
    >
      <h3 className="text-base font-semibold text-[var(--color-text-primary)]">New album</h3>
      <label className="block text-xs font-medium text-[var(--color-text-secondary)]">
        Title
        <input className={`${inputClass} mt-1`} value={title} onChange={(ev) => setTitle(ev.target.value)} required />
      </label>
      <label className="block text-xs font-medium text-[var(--color-text-secondary)]">
        Date
        <input className={`${inputClass} mt-1`} type="date" value={date} onChange={(ev) => setDate(ev.target.value)} required />
      </label>
      <label className="block text-xs font-medium text-[var(--color-text-secondary)]">
        Category
        <input className={`${inputClass} mt-1`} value={category} onChange={(ev) => setCategory(ev.target.value)} />
      </label>
      <div className="flex gap-2">
        <button type="button" className="min-h-11 flex-1 rounded-[var(--radius-md)] border border-[var(--color-border)] text-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" disabled={saving} className="min-h-11 flex-1 rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white disabled:opacity-50">
          Create album
        </button>
      </div>
    </form>
  );
}

function AlbumEditor({
  album,
  onBack,
  onChange,
  onError,
  askDelete,
  onDeleted,
}: {
  album: GalleryAlbum;
  onBack: () => void;
  onChange: () => Promise<void>;
  onError: (message: string) => void;
  askDelete: ReturnType<typeof usePinDelete>['ask'];
  onDeleted: () => void;
}) {
  const [title, setTitle] = useState(album.title);
  const [date, setDate] = useState(album.date);
  const [category, setCategory] = useState(album.category);
  const [description, setDescription] = useState(album.description);
  const [publishState, setPublishState] = useState(album.publishState);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [hiddenPhotoIds, setHiddenPhotoIds] = useState<Set<string>>(new Set());
  const photos = useMemo(
    () =>
      album.photos
        .filter((photo) => photo.position >= 0 && !hiddenPhotoIds.has(photo.id))
        .sort((a, b) => a.position - b.position),
    [album.photos, hiddenPhotoIds],
  );
  const photoCount = album.photos.filter((photo) => photo.position >= 0).length;

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/api/gallery/albums/${album.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title, date, category, description, publishState }),
      });
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save the album');
    } finally {
      setSaving(false);
    }
  };

  const upload = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = [...list];
    setProgress(`Preparing 1 of ${files.length}`);
    try {
      const ready: File[] = [];
      for (let i = 0; i < files.length; i++) {
        setProgress(`Preparing ${i + 1} of ${files.length}`);
        ready.push(await prepareContentPhotoFile(files[i]!));
      }
      setProgress(`Uploading ${files.length} photo${files.length === 1 ? '' : 's'}`);
      const form = new FormData();
      for (const file of ready) form.append('photos', file);
      await apiFetch(`/api/gallery/albums/${album.id}/photos`, { method: 'POST', body: form });
      await onChange();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not upload photos');
    } finally {
      setProgress(null);
    }
  };

  const reorder = async (next: GalleryPhoto[]) => {
    await apiFetch(`/api/gallery/albums/${album.id}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ photoIds: next.map((photo) => photo.id) }),
    });
    await onChange();
  };

  return (
    <div className="space-y-4">
      <button type="button" className="min-h-11 text-sm text-[var(--color-accent)]" onClick={onBack}>
        All albums
      </button>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="block text-xs font-medium text-[var(--color-text-secondary)]">
          Title
          <input className={`${inputClass} mt-1`} value={title} onChange={(ev) => setTitle(ev.target.value)} />
        </label>
        <label className="block text-xs font-medium text-[var(--color-text-secondary)]">
          Date
          <input className={`${inputClass} mt-1`} type="date" value={date} onChange={(ev) => setDate(ev.target.value)} />
        </label>
        <label className="block text-xs font-medium text-[var(--color-text-secondary)]">
          Category
          <input className={`${inputClass} mt-1`} value={category} onChange={(ev) => setCategory(ev.target.value)} />
        </label>
        <label className="block text-xs font-medium text-[var(--color-text-secondary)]">
          Status
          <select className={`${inputClass} mt-1`} value={publishState} onChange={(ev) => setPublishState(ev.target.value as 'draft' | 'published')}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </label>
        <label className="block text-xs font-medium text-[var(--color-text-secondary)] md:col-span-2">
          Description
          <textarea className={`${inputClass} mt-1`} rows={3} value={description} onChange={(ev) => setDescription(ev.target.value)} />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={saving} onClick={() => void save()} className="min-h-11 rounded-[var(--radius-md)] bg-[var(--color-accent)] px-4 text-sm font-medium text-white disabled:opacity-50">
          Save album
        </button>
        <button
          type="button"
          className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] px-4 text-sm"
          onClick={() => {
            askDelete({
              title: `Delete the album “${album.title}”?`,
              description: 'Its photos are deleted with it. This cannot be undone.',
              confirmLabel: 'Delete album',
              run: async (pin) => {
                await deleteGalleryAlbum(album.id, pin);
                pushScoringToast(albumDeletedToast(album.title, photoCount));
                onDeleted();
                void onChange();
              },
            });
          }}
        >
          Delete album
        </button>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Photos</h3>
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 text-sm">
            <ImagePlus className="h-4 w-4" />
            Add photos
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="sr-only"
              onChange={(ev) => {
                void upload(ev.target.files);
                ev.target.value = '';
              }}
            />
          </label>
        </div>
        <p className="text-xs text-[var(--color-text-tertiary)]">
          JPEG, PNG, or WebP. Large photos are compressed to about 2000px on the long edge.
        </p>
        {progress && <p className="text-sm text-[var(--color-text-secondary)]">{progress}</p>}
        <ul className="space-y-3">
          {photos.map((photo, index) => (
            <li
              key={photo.id}
              draggable
              onDragStart={(ev) => ev.dataTransfer.setData('text/plain', String(index))}
              onDragOver={(ev) => ev.preventDefault()}
              onDrop={(ev) => {
                ev.preventDefault();
                const from = Number(ev.dataTransfer.getData('text/plain'));
                if (!Number.isInteger(from) || from === index) return;
                const next = [...photos];
                const [moved] = next.splice(from, 1);
                if (!moved) return;
                next.splice(index, 0, moved);
                void reorder(next);
              }}
              className="grid grid-cols-[5.5rem_1fr] gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-3"
            >
              {photo.url ? <GalleryThumb url={photo.url} /> : <div className="aspect-square rounded-[var(--radius-sm)] bg-[var(--color-surface-2)]" />}
              <div className="space-y-2">
                <input
                  className={inputClass}
                  defaultValue={photo.alt}
                  aria-label="Alt text"
                  placeholder="Alt text"
                  key={`${photo.id}-alt-${photo.alt}`}
                  onBlur={(ev) => {
                    if (ev.target.value === photo.alt) return;
                    void apiFetch(`/api/gallery/photos/${photo.id}`, {
                      method: 'PATCH',
                      body: JSON.stringify({ alt: ev.target.value }),
                    }).then(() => onChange());
                  }}
                />
                <input
                  className={inputClass}
                  defaultValue={photo.caption ?? ''}
                  aria-label="Caption"
                  placeholder="Caption"
                  key={`${photo.id}-cap-${photo.caption ?? ''}`}
                  onBlur={(ev) => {
                    if (ev.target.value === (photo.caption ?? '')) return;
                    void apiFetch(`/api/gallery/photos/${photo.id}`, {
                      method: 'PATCH',
                      body: JSON.stringify({ caption: ev.target.value }),
                    }).then(() => onChange());
                  }}
                />
                <div className="flex flex-wrap gap-1">
                  <button type="button" className="min-h-11 min-w-11 rounded-[var(--radius-md)] border border-[var(--color-border)]" aria-label="Move photo up" disabled={index === 0} onClick={() => {
                    const next = [...photos];
                    [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                    void reorder(next);
                  }}>
                    <ChevronUp className="mx-auto h-4 w-4" />
                  </button>
                  <button type="button" className="min-h-11 min-w-11 rounded-[var(--radius-md)] border border-[var(--color-border)]" aria-label="Move photo down" disabled={index === photos.length - 1} onClick={() => {
                    const next = [...photos];
                    [next[index + 1], next[index]] = [next[index]!, next[index + 1]!];
                    void reorder(next);
                  }}>
                    <ChevronDown className="mx-auto h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="min-h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 text-sm"
                    onClick={() => {
                      void apiFetch(`/api/gallery/albums/${album.id}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ coverPhotoId: photo.id }),
                      }).then(() => onChange());
                    }}
                  >
                    {album.coverPhotoId === photo.id ? 'Cover' : 'Set cover'}
                  </button>
                  <button
                    type="button"
                    className="min-h-11 min-w-11 rounded-[var(--radius-md)] border border-[var(--color-border)]"
                    aria-label="Delete photo"
                    onClick={() => {
                      askDelete({
                        title: 'Delete this photo?',
                        description: 'This cannot be undone.',
                        confirmLabel: 'Delete photo',
                        run: async (pin) => {
                          await deleteGalleryPhoto(photo.id, pin);
                          setHiddenPhotoIds((prev) => new Set(prev).add(photo.id));
                          pushScoringToast(photoDeletedToast());
                          void onChange();
                        },
                      });
                    }}
                  >
                    <Trash2 className="mx-auto h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
